<template>
  <div class="join-form">
    <v-form ref="joinForm" v-model="isFormValid" @submit.prevent="submitForm">
      <v-row class="join-form__row">
        <template v-if="isDeveloper || !isInviteDeveloper">
          <v-col class="join-form__col">
            <v-select
              v-model="newDeveloper.status"
              class="form-field"
              :class="{'join-form__status' : isDeveloper}"
              :items="featuresData.status"
              :rules="[rules.required]"
              dense
              :disabled="isDeveloper"
              item-color="blue"
              :label="statusLabel"
              placeholder="Choose type"
            />
          </v-col>
        </template>
        <v-col class="join-form__col">
          <v-text-field
            v-model="newDeveloper.email"
            class="form-field"
            :rules="[rules.required, rules.email]"
            validate-on-blur
            dense
            label="Email Address*"
            placeholder="Type email"
          />
        </v-col>
      </v-row>

      <v-row class="join-form__row">
        <v-col class="join-form__col">
          <v-text-field
            v-model="newDeveloper.first_name"
            class="form-field"
            :rules="[rules.required]"
            dense
            label="First name*"
            placeholder="Type name..."
          />
        </v-col>
        <v-col class="join-form__col">
          <v-text-field
            v-model="newDeveloper.last_name"
            class="form-field"
            :rules="[rules.required]"
            dense
            label="Last name*"
            placeholder="Type name..."
          />
        </v-col>
      </v-row>

      <v-row class="join-form__row">
        <v-col class="join-form__col">
          <v-text-field
            v-model="newDeveloper.city"
            class="form-field"
            :rules="[rules.required]"
            dense
            label="City*"
            placeholder="Type city..."
          />
        </v-col>
        <v-col class="join-form__col">
          <v-autocomplete
            v-model="newDeveloper.nationality"
            class="form-field"
            :items="nationalities"
            item-value="nationality"
            item-text="nationality"
            :rules="[rules.required]"
            dense
            item-color="blue"
            label="Nationality*"
            placeholder="Choose nationality"
          />
        </v-col>
      </v-row>

      <v-row class="join-form__row">
        <v-col class="join-form__col">
          <v-autocomplete
            v-model="newDeveloper.roles"
            class="form-field"
            item-color="blue"
            :items="featuresData.roles"
            :rules="[rules.required]"
            dense
            multiple
            label="Primary Roles*"
            placeholder="Choose skills"
            :search-input.sync="rolesSearchInput"
            @change="rolesSearchInput=''"
          >
          </v-autocomplete>
        </v-col>
      </v-row>

      <v-row class="join-form__row">
        <v-col class="join-form__col">
          <v-combobox
            v-model="newDeveloper.top_skills"
            class="form-field"
            item-color="blue"
            :items="featuresData.top_skills"
            :rules="[rules.required]"
            dense
            multiple
            label="Top skills*"
            placeholder="Choose skills"
            :search-input.sync="skillsSearchInput"
            @change="skillsSearchInput=''"
          >
          </v-combobox>
        </v-col>
      </v-row>

      <v-row class="join-form__row">
        <v-col class="join-form__col">
          <v-select
            v-if="isDeveloper"
            v-model="newDeveloper.languages"
            class="form-field"
            item-color="blue"
            :items="languagesArray"
            :item-text="getLanguageValue"
            :rules="[rules.required]"
            return-object
            dense
            multiple
            label="Language*"
            placeholder="Choose language"
          >
          </v-select>
        </v-col>
      </v-row>

      <v-row class="join-form__row">
        <v-col class="join-form__col">
          <v-select
            v-model="newDeveloper.level"
            class="form-field"
            :items="featuresData.level"
            :rules="[rules.required]"
            dense
            item-color="blue"
            label="Level*"
            placeholder="Select your level"
          />
        </v-col>
        <v-col class="join-form__col">
          <v-text-field
            v-model="newDeveloper.experience_in_years"
            class="form-field"
            :rules="[rules.required]"
            dense
            label="Experience*"
            placeholder="Type your experience in years"
            type="number"
          />
        </v-col>
      </v-row>

      <v-row class="join-form__row">
        <v-col class="join-form__col">
          <v-text-field
            v-model="newDeveloper.rate"
            class="form-field"
            :rules="[rules.required, rules.positive]"
            dense
            label="Rate*"
            placeholder="Type hourly rate..."
            suffix="/ h"
            type="number"
          />
        </v-col>
        <v-col class="join-form__col">
          <v-text-field
            v-model="newDeveloper.currency"
            class="form-field"
            dense
            disabled
            item-color="blue"
            label="Currency"
            value="PLN"
          />
        </v-col>
      </v-row>

      <v-row class="join-form__row" v-if="!isDeveloper">
        <v-col class="join-form__col">
          <v-select
            v-model="newDeveloper.language_name"
            class="form-field"
            :items="featuresData.language_name"
            :rules="[rules.required]"
            dense
            item-color="blue"
            label="Language*"
            placeholder="Choose language"
          />
        </v-col>
        <v-col class="join-form__col">
          <v-select
            v-model="newDeveloper.language_level"
            class="form-field"
            :items="featuresData.language_level"
            :rules="[rules.required]"
            dense
            item-color="blue"
            label="Level*"
            placeholder="Choose level"
          />
        </v-col>
      </v-row>

      <v-row v-if="acceptance && isDeveloper" class="join-form__row">
        <v-col class="join-form__col">
          <div class="accept-terms">
            <v-checkbox
              v-model="newDeveloper.sensitive_data_accepted"
              class="accept-terms__checkbox"
              color="blue"
              dense
              hide-details
            >
              <template #label>
                <span class="accept-terms__acceptance">
                  I want to take part in the recruitment conducted by Applover Software House
                  based in Wrocław, ul. Świeradowska 77, 50-599 and I consent to the processing
                  of my data other than those regulated by law.<br>
                  I can withdraw my consent at any time, but actions performed prior to its
                  withdrawal will remain legal.
                </span>
              </template>
            </v-checkbox>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="acceptance" class="join-form__row">
        <v-col class="join-form__col">
          <div class="accept-terms">
            <v-checkbox
              v-model="newDeveloper.personal_data_accepted"
              class="accept-terms__checkbox"
              color="blue"
              dense
              hide-details
            >
              <template #label>
                <span class="accept-terms__acceptance">
                  I’ve read the privacy policy of Applover Software House based in Wrocław,
                  available
                </span>
              </template>
            </v-checkbox>
            <a
              class="accept-terms__link ml-1"
              target="_blank"
              href="https://applover.com/wp-content/uploads/2020/07/privacy-policy-Applover-06.2020.pdf"
            >
              <b>here</b>
            </a>
          </div>
        </v-col>
      </v-row>

      <v-row class="join-form__row mt-4">
        <template v-if="!isDeveloper">
          <v-col class="join-form__col">
            <v-btn
              v-if="!acceptance"
              color="button"
              large
              :disabled="processing"
              width="100%"
              @click="goBack"
            >
              <v-icon small class="mr-3">mdi-arrow-left</v-icon> Back
            </v-btn>
          </v-col>
        </template>
        <v-col class="join-form__col col-6">
          <v-btn
            color="blue"
            class="white--text"
            large
            :disabled="acceptance && !agreementsAccepted"
            :loading="processing"
            width="100%"
            @click="submitForm">
            {{ submitName }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import validationRules from '@/helpers/validators';
import nationalities from '@/helpers/nationalities';

const rules = validationRules;

export default {
  props: {
    submitName: {
      type: String,
      required: false,
      default: 'Submit',
    },
    acceptance: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDeveloper: {
      type: Boolean,
      default: false,
    },
    processing: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      rules,
      nationalities,
      isFormValid: true,
      isFormAccepted: false,
      statusLabel: this.isDeveloper ? 'Your status' : 'Type*',
      newDeveloper: {
        status: this.isDeveloper ? 'Freelancer' : '',
        roles: [],
        first_name: '',
        last_name: '',
        top_skills: [],
        rate: '',
        currency: 'PLN',
        city: '',
        nationality: '',
        email: '',
        languages: [],
        language_name: '',
        language_level: '',
        personal_data_accepted: false,
      },
      rolesSearchInput: '',
      skillsSearchInput: '',
    };
  },

  computed: {
    ...mapState('features', [
      'featuresData',
    ]),

    isInviteDeveloper() {
      return this.$route.params.token;
    },

    agreementsAccepted() {
      if (!this.isDeveloper) {
        return this.newDeveloper.personal_data_accepted;
      }
      return this.newDeveloper.personal_data_accepted && this.newDeveloper.sensitive_data_accepted;
    },
    languagesArray() {
      const arr = [];
      this.featuresData.language_name.forEach((languageName) => {
        this.featuresData.language_level.forEach((languageLevel) => {
          arr.push({ name: languageName, level: languageLevel });
        });
      });
      return arr;
    },
  },

  methods: {
    getLanguageValue(item) {
      return `${item.name} - ${item.level}`;
    },

    goBack() {
      this.$emit('close');
    },

    submitForm() {
      this.$refs.joinForm.validate();

      if (this.isFormValid) {
        this.$emit('submit', this.newDeveloper);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.join-form {
  &__row {
    margin-right: -8px;
    margin-left: -8px;
  }

  &__col {
    padding: 0 8px 8px;
  }
  &__status::v-deep {
    .v-input__slot {
      background-color: $color--secondary !important;
    }
    .v-label,
    .v-select__selection--disabled {
      color: $main--text !important;
    }
    .v-icon--disabled {
      display: none;
    }
  }
}
.accept-terms {
  color: $color--primary;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: flex-start;

  &__checkbox {
    margin-top: 0;
    padding-top: 0px;
  }

  &__acceptance {
    color: $color--primary;
    font-size: 12px;
    line-height: 22px;
    margin-bottom: 12px;
  }

  &__link {
    line-height: 24px;
  }
}

</style>
